import { useId } from 'react';
import { pointsOf, wireframe, type WireframeKey } from '@ce/core';

/**
 * A wireframe motif used as a quiet ground behind buttons and screens.
 *
 * Strokes are masked by a radial gradient, so every line fades out before it
 * reaches an edge and nothing ends in a hard cut. Colours come from the token
 * layer, so the motif follows the attendee's theme like the rest of the chrome.
 *
 * `preserveAspectRatio="none"` is deliberate: these are texture, not diagrams,
 * and letting them stretch to the container beats letterboxing them.
 */
export function Wireframe({
  art,
  opacity = 1,
  strokeWidth = 1,
  fade = 'radial',
  style,
}: {
  art: WireframeKey;
  opacity?: number;
  strokeWidth?: number;
  /** radial for buttons, top for full-screen grounds that must not fight content. */
  fade?: 'radial' | 'top';
  style?: React.CSSProperties;
}) {
  const id = useId().replace(/:/g, '');
  const { viewBox, primary, secondary, accent } = wireframe(art);
  const [w, h] = viewBox;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden
      focusable="false"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'none', opacity, ...style,
      }}
    >
      <defs>
        <radialGradient id={`${id}-r`} cx="50%" cy="50%" r="62%">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="58%" stopColor="#fff" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-t`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.85" />
          <stop offset="70%" stopColor="#fff" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={`${id}-m`}>
          <rect
            x="0" y="0" width={w} height={h}
            fill={`url(#${id}-${fade === 'radial' ? 'r' : 't'})`}
          />
        </mask>
      </defs>

      <g
        mask={`url(#${id}-m)`}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      >
        {secondary.map((line, i) => (
          <polyline
            key={`s${i}`}
            points={pointsOf(line)}
            stroke="var(--color-text)"
            strokeOpacity={0.16}
            strokeWidth={strokeWidth * 0.75}
          />
        ))}
        {primary.map((line, i) => (
          <polyline
            key={`p${i}`}
            points={pointsOf(line)}
            stroke="var(--color-text)"
            strokeOpacity={0.3}
            strokeWidth={strokeWidth}
          />
        ))}
        {accent.map((line, i) => (
          <polyline
            key={`a${i}`}
            points={pointsOf(line)}
            stroke="var(--color-accent)"
            strokeOpacity={0.55}
            strokeWidth={strokeWidth}
          />
        ))}
      </g>
    </svg>
  );
}
